import {
	CloseButton,
	Dialog,
	DialogBackdrop,
	DialogPanel,
} from "@headlessui/react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { ClassSearch } from "./class-search.tsx";

const ROLE_LINKS = [
	{ to: "/", label: "OVERVIEW" },
	{ to: "/scout", label: "SCOUT" },
	{ to: "/support", label: "SUPPORT" },
	{ to: "/offense", label: "OFFENSE" },
	{ to: "/wildcard", label: "WILDCARD" },
] as const;

const UTILITY_LINKS = [
	{ to: "/owned-items", label: "OWNED ITEMS", icon: "military_tech" },
	{ to: "/settings", label: "SETTINGS", icon: "settings" },
] as const;

const rowBase =
	"flex min-h-11 items-center gap-3 border-l-4 border-transparent bg-surface-container-lowest px-4 font-headline text-base tracking-tighter text-outline uppercase transition-colors hover:bg-surface-container-low hover:text-on-surface";
const rowActive =
	"flex min-h-11 items-center gap-3 border-l-4 border-primary-container bg-surface-container-low px-4 font-headline text-base tracking-tighter text-primary-container uppercase";

interface MobileMenuProps {
	open: boolean;
	onClose: () => void;
}

export function MobileMenu({ open, onClose }: MobileMenuProps) {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const previous = useRef(pathname);

	useEffect(() => {
		if (pathname !== previous.current) {
			previous.current = pathname;
			onClose();
		}
	}, [pathname, onClose]);

	return (
		<Dialog open={open} onClose={onClose} className="relative z-50">
			<DialogBackdrop className="fixed inset-0 bg-surface/80" />
			<div className="fixed inset-0 flex justify-end">
				<DialogPanel className="flex h-full w-full max-w-xs flex-col border-l-4 border-primary-container bg-surface">
					<div className="flex items-center justify-between border-b-2 border-surface-container-highest bg-surface-container-lowest px-4 py-3">
						<span className="font-mono text-[10px] tracking-widest text-primary-container uppercase">
							{"NAVIGATION //"}
						</span>
						<CloseButton
							aria-label="Close menu"
							className="inline-grid h-11 w-11 place-items-center leading-none text-outline transition-all hover:bg-surface-container-highest hover:text-on-surface active:translate-x-0.5 active:translate-y-0.5"
						>
							<span className="material-symbols-outlined block text-[20px] leading-none">
								close
							</span>
						</CloseButton>
					</div>
					<div className="border-b-2 border-surface-container-highest bg-surface-container-lowest p-4">
						<ClassSearch expand />
					</div>
					<nav className="flex flex-col">
						{ROLE_LINKS.map((link) => (
							<Link
								key={link.to}
								to={link.to}
								className={rowBase}
								activeProps={{ className: rowActive }}
								activeOptions={{ exact: link.to === "/" }}
							>
								{link.label}
							</Link>
						))}
					</nav>
					<div className="h-3 bg-surface" />
					<nav className="flex flex-col">
						{UTILITY_LINKS.map((link) => (
							<Link
								key={link.to}
								to={link.to}
								className={rowBase}
								activeProps={{ className: rowActive }}
							>
								<span className="material-symbols-outlined block text-[18px] leading-none">
									{link.icon}
								</span>
								{link.label}
							</Link>
						))}
					</nav>
				</DialogPanel>
			</div>
		</Dialog>
	);
}
